/**
 * WaypointPickerOverlay — in-Canvas click-to-place waypoint picker + always-on-top
 * numbered markers and planned route line for the Generate Body Animation flow.
 *
 * Everything here is IMPERATIVE THREE, cold-rebuilt inside the waypointPickerStore
 * subscription — NO useFrame, NO React state for markers (the waypoint list is
 * cold user-cadence state owned by the store). The always-on-top recipe
 * (renderOrder 9999, depthTest/depthWrite false) mirrors NavDebugOverlay so the
 * markers/line draw over the gaussian-splat environment.
 *
 * Armed CTRL/CMD+left-clicks are swallowed in the CAPTURE phase on the canvas's
 * PARENT so they genuinely precede OrbitControls'/inputComponent's own canvas
 * listeners; a plain left-drag (no modifier) and right/middle-button orbit pass
 * through untouched so the camera stays fully controllable while picking is armed.
 * While armed, `contextmenu` on the canvas is also suppressed: on macOS
 * Ctrl+left-click IS the OS secondary-click gesture, so a pick fires contextmenu
 * alongside the pointerdown and would pop the native menu over the viewport.
 * Off-mesh clicks are rejected loudly (pickWaypoint throws -> store.setPickError
 * -> red in the wizard).
 */
import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { waypointPickerStore } from '../lib/waypointPickerStore'
import { avatarFloor } from '../lib/avatarFloor'
import { pickWaypoint } from '../lib/bodyMotionPath'
import { WAYPOINT_COLORS } from '../lib/waypointColors'
import { buildAnchorGizmo } from './routeAnchorGizmo'

// Module-scope scratch — no per-event allocation.
const _raycaster = new THREE.Raycaster()
const _plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
const _hit = new THREE.Vector3()
const _ndc = new THREE.Vector2()

export default function WaypointPickerOverlay() {
  const gl = useThree(s => s.gl)
  const camera = useThree(s => s.camera)
  const scene = useThree(s => s.scene)
  const groupRef = useRef(null)

  // ── Effect A — armed click capture ────────────────────────────────────────
  useEffect(() => {
    let attached = false
    let el = null
    let tip = null // cursor-following usage hint (DOM, appended to body)

    const moveTip = (e) => {
      if (!tip) return
      tip.style.display = 'block'
      tip.style.left = `${e.clientX}px`
      tip.style.top = `${e.clientY - 14}px`
    }
    const hideTip = () => { if (tip) tip.style.display = 'none' }

    // macOS fires `contextmenu` for Ctrl+left-click (the OS secondary-click
    // gesture) IN ADDITION to the pointerdown we place the waypoint from, so a
    // Mac pick would place the waypoint AND pop the native menu over the
    // viewport. Suppress it on the canvas for as long as the picker is armed —
    // owned here rather than leaning on OrbitControls'/inputComponent's own
    // contextmenu preventDefault, which is only attached in some modes. A
    // right-drag orbit loses nothing: the menu is noise there too, and the
    // listener is removed the moment the picker disarms.
    const onContextMenu = (e) => { e.preventDefault() }

    const onDown = (e) => {
      // The listener is on the canvas's PARENT (capture phase), so it also sees
      // clicks on any sibling DOM the r3f wrapper might hold — only act on the
      // canvas itself, never raycast a waypoint through overlaid UI.
      if (e.target !== gl.domElement) return
      if (e.button !== 0) return // right/middle orbit passes through untouched
      // Require Ctrl/Cmd: a plain left-drag must still orbit the camera while
      // armed, so only a modified left-click places a waypoint.
      if (!e.ctrlKey && !e.metaKey) return
      e.stopImmediatePropagation()
      e.preventDefault()
      const rect = gl.domElement.getBoundingClientRect()
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1
      const nm = window.__NAVMESH_INSTANCE__ || null
      // avatarFloor, NOT nm.floorY — a baked navmesh's floorY is the floor
      // height in the BAKE's space, so on a posed environment (Startup_Level:
      // -6.2 against a world floor of 0) this plane sat metres below the visible
      // floor and every pick landed metres from the cursor. See the same note in
      // ViewportContextMenu.
      _plane.constant = -avatarFloor.getY()
      _raycaster.setFromCamera(_ndc.set(nx, ny), camera)
      if (!_raycaster.ray.intersectPlane(_plane, _hit)) {
        waypointPickerStore.setPickError('click did not hit the ground plane')
        return
      }
      try {
        const wp = pickWaypoint(nm, _hit.x, _hit.z, { mode: waypointPickerStore.getState().pathMode })
        waypointPickerStore.setPickError(null)
        waypointPickerStore.addWaypoint(wp)
      } catch (err) {
        console.warn('[waypointPicker]', err)
        waypointPickerStore.setPickError(String(err.message ?? err))
      }
    }

    const attach = () => {
      // Parent (not the canvas): a capture listener directly on the canvas runs
      // in registration order with OrbitControls'/inputComponent's own canvas
      // listeners during the target phase; only the PARENT's capture phase
      // genuinely precedes them.
      el = gl.domElement.parentElement ?? gl.domElement
      el.addEventListener('pointerdown', onDown, { capture: true })
      gl.domElement.style.cursor = 'crosshair'
      // Cursor-following usage hint — sits just above the pointer over the
      // viewport, hidden when the pointer leaves the canvas.
      tip = document.createElement('div')
      tip.textContent = 'Ctrl + Left Click'
      Object.assign(tip.style, {
        position: 'fixed', transform: 'translate(-50%, -100%)',
        padding: '3px 8px', borderRadius: '4px', whiteSpace: 'nowrap',
        background: 'rgba(11,13,21,.92)', border: '1px solid var(--m-line)',
        color: WAYPOINT_COLORS.point.hex, font: '700 11px/1 var(--m-mono)', letterSpacing: '.04em',
        pointerEvents: 'none', zIndex: '10000', display: 'none',
      })
      document.body.appendChild(tip)
      gl.domElement.addEventListener('pointermove', moveTip)
      gl.domElement.addEventListener('pointerleave', hideTip)
      gl.domElement.addEventListener('contextmenu', onContextMenu)
      attached = true
    }
    const detach = () => {
      if (!attached) return
      el?.removeEventListener('pointerdown', onDown, { capture: true })
      gl.domElement.style.cursor = ''
      gl.domElement.removeEventListener('pointermove', moveTip)
      gl.domElement.removeEventListener('pointerleave', hideTip)
      gl.domElement.removeEventListener('contextmenu', onContextMenu)
      if (tip) { tip.remove(); tip = null }
      attached = false
    }

    const sync = () => {
      const armed = waypointPickerStore.getState().armed
      if (armed && !attached) attach()
      else if (!armed && attached) detach()
    }
    const unsub = waypointPickerStore.subscribe(sync)
    sync()
    return () => { unsub(); detach() }
  }, [gl, camera])

  // ── Effect B — markers + route line (cold rebuild on store change) ─────────
  useEffect(() => {
    const teardown = () => {
      const g = groupRef.current
      if (!g) return
      g.traverse((o) => {
        o.geometry?.dispose?.()
        if (o.material) {
          o.material.map?.dispose?.() // sprite CanvasTexture
          o.material.dispose?.()
        }
      })
      scene.remove(g)
      groupRef.current = null
    }

    // The store emits on every mutation (arm/disarm/pickError/setPathMode too),
    // but markers only depend on the waypoints + route. Both are replaced by a
    // fresh reference whenever they actually change, so an identity check skips
    // the (canvas/CanvasTexture-allocating) rebuild on unrelated emits.
    let lastWaypoints, lastRoute
    const rebuild = () => {
      const { waypoints, route } = waypointPickerStore.getState()
      if (waypoints === lastWaypoints && route === lastRoute) return
      lastWaypoints = waypoints
      lastRoute = route
      teardown()
      if (!waypoints || waypoints.length === 0) {
        if (typeof window !== 'undefined') window.__WAYPOINT_MARKERS__ = null
        return
      }
      const group = new THREE.Group()
      group.renderOrder = 9999
      const floorY = avatarFloor.getY() // world floor, not the bake's own floorY

      for (let i = 0; i < waypoints.length; i++) {
        const w = waypoints[i]
        // Numbered diamond marker.
        const mesh = new THREE.Mesh(
          new THREE.OctahedronGeometry(0.09),
          new THREE.MeshBasicMaterial({
            color: (i === 0 ? WAYPOINT_COLORS.start : WAYPOINT_COLORS.point).num,
            depthTest: false, depthWrite: false, transparent: true, opacity: 0.95,
          }),
        )
        mesh.position.set(w.x, floorY + 0.12, w.z)
        mesh.renderOrder = 9999
        group.add(mesh)

        // Numbered sprite label.
        const cnv = document.createElement('canvas')
        cnv.width = 64; cnv.height = 64
        const ctx = cnv.getContext('2d')
        ctx.fillStyle = WAYPOINT_COLORS.label.hex
        ctx.font = 'bold 44px monospace'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(i), 32, 34)
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
          map: new THREE.CanvasTexture(cnv), depthTest: false, transparent: true,
        }))
        sprite.position.set(w.x, floorY + 0.35, w.z)
        sprite.scale.set(0.25, 0.25, 0.25)
        sprite.renderOrder = 9999
        group.add(sprite)
      }

      // Planned route line. Amber when straight mode (obstacle-ignored cue).
      // Draw at the marker floor height, not each point's own y: straight-mode
      // planning hardcodes p[1]=0, which would detach the line from its markers
      // in an environment whose navmesh floorY isn't ~0.
      if (route?.path?.length >= 2) {
        const pts = route.path.map(p => new THREE.Vector3(p[0], floorY + 0.05, p[2]))
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(pts),
          new THREE.LineBasicMaterial({
            color: (route.mode === 'straight' ? WAYPOINT_COLORS.routeStraight : WAYPOINT_COLORS.routeNavmesh).num,
            // transparent so it renders in the LATE transparent pass alongside the
            // markers — an opaque line draws before the gaussian-splat pass and the
            // splats paint over it regardless of renderOrder/depthTest.
            depthTest: false, depthWrite: false, transparent: true,
          }),
        )
        line.renderOrder = 9999
        group.add(line)
      }

      // Heading ticks — which way the character is being ASKED to face at each
      // sampled anchor, not which way the path goes. The two differ over the
      // opening frames (the heading ramp eases out of the avatar's real facing)
      // and that difference is the whole reason this is worth drawing.
      //
      // One LineSegments for all of them: a Line per tick would be a draw call
      // per tick on a path that can carry dozens.
      // Root points + heading ticks: what the request will actually carry.
      for (const o of buildAnchorGizmo(route?.headings, floorY)) group.add(o)

      scene.add(group)
      groupRef.current = group
      if (typeof window !== 'undefined') window.__WAYPOINT_MARKERS__ = group
    }

    const unsub = waypointPickerStore.subscribe(rebuild)
    rebuild()
    return () => {
      unsub()
      teardown()
      if (typeof window !== 'undefined') delete window.__WAYPOINT_MARKERS__
    }
  }, [scene])

  return null
}
